// Simple configuration - just specify hostname, shortId, and description
const REDIRECT_CONFIG = {
  paths: {
    paymentReport: '/payment-report',
    shortPayment: '/s',
  },
  domains: [
    {
      hostname: 'payme.chanilla.dk',
      shortId: 'V0TDNP',
      description: 'Chanilla payment redirect',
    },
    {
      hostname: 'pay.technical.pm',
      shortId: '12345',
      description: 'Technical PM payment report redirect',
    },
  ],
} as const;

export type RedirectType = 'payment-report' | 'short-payment';

export interface DomainRedirectConfig {
  hostname: string;
  redirectPath: string;
  redirectType: RedirectType;
  description?: string;
}

// Generate flat configuration array with both paths for each domain
export const domainRedirectConfigs: DomainRedirectConfig[] = REDIRECT_CONFIG.domains.flatMap((domain) => [
  {
    hostname: domain.hostname,
    redirectPath: `${REDIRECT_CONFIG.paths.shortPayment}/${domain.shortId}`,
    redirectType: 'short-payment',
    description: domain.description,
  },
  {
    hostname: domain.hostname,
    redirectPath: `${REDIRECT_CONFIG.paths.paymentReport}/${domain.shortId}`,
    redirectType: 'payment-report',
    description: `${domain.description} (report)`,
  },
]);

/**
 * Get redirect path for a given hostname
 * @param hostname - The hostname to check for redirect
 * @returns The redirect path if found, null otherwise
 */
export function getRedirectPath(hostname: string): string | null {
  const config = domainRedirectConfigs.find((config) => config.hostname === hostname);
  return config ? config.redirectPath : null;
}

/**
 * Get redirect path for a given hostname and type
 * @param hostname - The hostname to check for redirect
 * @param redirectType - The type of redirect to get
 * @returns The redirect path if found, null otherwise
 */
export function getRedirectPathByType(hostname: string, redirectType: RedirectType): string | null {
  const config = domainRedirectConfigs.find(
    (config) => config.hostname === hostname && config.redirectType === redirectType,
  );
  return config ? config.redirectPath : null;
}

/**
 * Get redirect configurations for a specific hostname
 * @param hostname - The hostname to get configs for
 * @returns Array of redirect configurations for the hostname
 */
export function getRedirectConfigsForHostname(hostname: string): DomainRedirectConfig[] {
  return domainRedirectConfigs.filter((config) => config.hostname === hostname);
}

/**
 * Get redirect configurations for a specific hostname and type
 * @param hostname - The hostname to get configs for
 * @param redirectType - The type of redirect to get
 * @returns Array of redirect configurations for the hostname and type
 */
export function getRedirectConfigsForHostnameAndType(
  hostname: string,
  redirectType: RedirectType,
): DomainRedirectConfig[] {
  return domainRedirectConfigs.filter((config) => config.hostname === hostname && config.redirectType === redirectType);
}

/**
 * Get the primary redirect path for a hostname (first configured path)
 * @param hostname - The hostname to get primary path for
 * @returns The primary redirect path or null if not found
 */
export function getPrimaryRedirectPath(hostname: string): string | null {
  const configs = getRedirectConfigsForHostname(hostname);
  return configs.length > 0 ? configs[0].redirectPath : null;
}

/**
 * Get the short payment path for a hostname
 * @param hostname - The hostname to get short payment path for
 * @returns The short payment path or null if not found
 */
export function getShortPaymentPath(hostname: string): string | null {
  return getRedirectPathByType(hostname, 'short-payment');
}

/**
 * Get the payment report path for a hostname
 * @param hostname - The hostname to get payment report path for
 * @returns The payment report path or null if not found
 */
export function getPaymentReportPath(hostname: string): string | null {
  return getRedirectPathByType(hostname, 'payment-report');
}

/**
 * Get domain configuration by hostname
 * @param hostname - The hostname to get config for
 * @returns Domain config if found, null otherwise
 */
export function getDomainConfig(hostname: string): (typeof REDIRECT_CONFIG.domains)[number] | null {
  const domain = REDIRECT_CONFIG.domains.find((domain) => domain.hostname === hostname);
  return domain || null;
}

/**
 * Get the single redirect configuration object
 * @returns The complete redirect configuration
 */
export function getRedirectConfig() {
  return REDIRECT_CONFIG;
}
